import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cartService from '../services/cart.service';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  // Ensure session ID for guest carts
  useEffect(() => {
    if (!localStorage.getItem('ebuywiz_session')) {
      localStorage.setItem('ebuywiz_session', uuidv4());
    }
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await cartService.get();
      setCart(data.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, isAuthenticated]);

  const addToCart = async (productId, quantity = 1, variantId = null) => {
    const { data } = await cartService.add(productId, quantity, variantId);
    setCart(data.data);
    return data.data;
  };

  const updateQuantity = async (productId, quantity) => {
    const { data } = await cartService.updateItem(productId, quantity);
    setCart(data.data);
  };

  const removeItem = async (productId) => {
    const { data } = await cartService.removeItem(productId);
    setCart(data.data);
  };

  const clearCart = async () => {
    await cartService.clear();
    setCart({ items: [], savedForLater: [] });
  };

  const saveForLater = async (productId) => {
    const { data } = await cartService.saveForLater(productId);
    setCart(data.data);
  };

  const moveToCart = async (productId) => {
    const { data } = await cartService.moveToCart(productId);
    setCart(data.data);
  };

  const removeSaved = async (productId) => {
    const { data } = await cartService.removeSaved(productId);
    setCart(data.data);
  };

  const itemCount = cart.items?.reduce((count, item) => count + item.quantity, 0) || 0;
  const subtotal = cart.items?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
  const savedCount = cart.savedForLater?.length || 0;

  const value = {
    cart,
    loading,
    itemCount,
    subtotal,
    savedCount,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    saveForLater,
    moveToCart,
    removeSaved,
    fetchCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
